import { _converse, api, converse } from '@converse/headless';
import BaseModal from 'plugins/modal/modal.js';
import tplAddMuc from './templates/add-muc.js';
import { __ } from 'i18n';

import '../styles/add-muc-modal.scss';

const u = converse.env.utils;
const { Strophe } = converse.env;

export default class AddMUCModal extends BaseModal {
    static properties = {
        model: { type: Object },
        step: { type: Number },
        selectedContacts: { type: Array },
        searchQuery: { type: String },
        error_message: { type: String },
        _submitting: { type: Boolean },
    };

    constructor(options) {
        super(options);
        this.step = 1;
        this.selectedContacts = [];
        this.searchQuery = '';
        this.error_message = '';
        this._submitting = false;
        this.model = options?.model || {};
    }

    initialize() {
        super.initialize();
        this.requestUpdate();
    }

    renderModalFooter() {
        return '';
    }

    renderModal() {
        return tplAddMuc(this);
    }

    getModalTitle() {
        return this.step === 1 ? __('Вибір учасників') : __('Назва групи');
    }

    // --- Логика выбора участников ---
    onSearch(ev) {
        this.searchQuery = ev.target.value;
    }

    get rosterContacts() {
        const query = this.searchQuery.toLowerCase();
        return (_converse.roster?.models || []).filter((m) => {
            if (_converse.roster.isSelf(m.get('jid'))) return false;
            const name = (m.get('fullname') || m.get('jid') || '').toLowerCase();
            return name.includes(query);
        });
    }

    toggleContact(jid) {
        if (this.selectedContacts.includes(jid)) {
            this.selectedContacts = this.selectedContacts.filter((j) => j !== jid);
        } else {
            this.selectedContacts = [...this.selectedContacts, jid];
        }
    }

    goToNextStep() {
        if (this.selectedContacts.length === 0) {
            api.alert('info', __('Attention'), __('Please select at least one participant'));
            return;
        }
        this.step = 2;
    }

    // --- Утилита для создания валидного JID из названия ---
    normalizeNode(name) {
        if (!name) return '';
        // 1. Транслитерация (опционально, если сервер не любит кириллические JID)
        // Если хотите оставить кириллицу в JID, пропустите этот шаг

        return name
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-') // Заменяем пробелы на дефисы
            .replace(/[^\w\d\u0400-\u04FF-]/g, '') // Оставляем только буквы (в т.ч. кириллицу), цифры и дефис
            .replace(/-+/g, '-'); // Убираем двойные дефисы
    }

    async openChatRoom(ev) {
        ev.preventDefault();
        if (this._submitting) return;

        const { escapeNode, getNodeFromJid, getDomainFromJid } = Strophe;
        const form = ev.target;
        const data = new FormData(form);

        const rawName = data.get('chatroom')?.trim();

        if (!rawName) {
            this.error_message = __('Group name is required');
            return;
        }

        this._submitting = true;

        try {
            const nick = _converse.exports.getDefaultMUCNickname();
            let jid;

            // 1. Формируем технический JID (оставляем исправленную логику для кириллицы)
            if (api.settings.get('locked_muc_domain') || !u.isValidJID(rawName)) {
                const muc_service = await u.muc.getDefaultMUCService();
                let node = this.normalizeNode(rawName);
                if (!node) {
                    node = 'group-' + Math.random().toString(36).substring(2, 9);
                }
                jid = `${escapeNode(node)}@${muc_service}`.toLowerCase();
            } else {
                jid = `${escapeNode(getNodeFromJid(rawName))}@${getDomainFromJid(rawName)}`.toLowerCase();
            }

            if (jid.startsWith('@')) {
                throw new Error(__('Could not generate a valid JID for this group name'));
            }

            // 2. Открываем комнату
            const roomModel = await api.rooms.open(
                jid,
                {
                    nick,
                    name: rawName,
                    auto_configure: true,
                    roomconfig: {
                        'muc#roomconfig_roomname': rawName,
                        'roomname': rawName,
                    },
                },
                true,
            );

            // 3. Додаємо вибраних учасників у групу.
            // Affiliate them as members explicitly and unconditionally --
            // this is a brand-new group, so they should end up as real
            // members regardless of the room's membersonly setting, and
            // that feature flag may not even be populated yet this soon
            // after creation (disco#info is a separate async fetch).
            await roomModel.updateMemberLists(
                this.selectedContacts.map((contactJid) => ({ jid: contactJid, affiliation: 'member' })),
            );
            // directInvite() also does its own membersonly-conditional
            // affiliation (a harmless no-op here, already affiliated above)
            // and sends the actual invite notification (mediated + direct;
            // the mediated one is tagged 'group-created' automatically if
            // this room's session still has that flag set from creation --
            // by now the awaits above have given the server's self-presence
            // time to land). A raw XEP-0249 stanza, as this used to send,
            // never added anyone to the room's member list at all -- hence
            // the group ending up with none of the "selected" participants.
            this.selectedContacts.forEach((contactJid) => {
                roomModel.directInvite(contactJid);
            });

            // Логирование для проверки
            if (roomModel.get('connection_status') !== converse.ROOMSTATUS.ENTERED) {
                this.listenToOnce(roomModel, 'enter', () => {
                    console.log(`MUC "${rawName}" created successfully`);
                });
            }

            this.modal.hide();
        } catch (e) {
            console.error('MUC Creation Error:', e);
            this.error_message = e.message || __('An error occurred');
            this._submitting = false;
        }
    }
}

if (!customElements.get('converse-add-muc-modal')) {
    customElements.define('converse-add-muc-modal', AddMUCModal);
}
